// Sentry must initialize before any other code runs.
import './instrument';

import React from 'react';

import { reactErrorHandler } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';

import App from './App';
import { queryClient } from './lib/queryClient.util';
import { installEventSourcePolyfill } from './lib/sse/installEventSourcePolyfill';
import './styles/globals.css';
import { ThemeProvider } from './theme/components/theme-provider';
import { initWebVitals } from './utils/performance.util';

// Note (GW-11884): The eager `import './lib/awsCognito.util'` was removed. It had a
// stale "trigger Amplify configuration at module load" comment, but Amplify
// configuration is already deferred to first use via `ensureAmplifyConfigured()`
// (GW-6402), so the import had no side effect at module load — it only pulled
// aws-amplify onto the entry chunk. Amplify is still bundled (and configured
// synchronously on the first auth call) via the store slices and API clients that
// import `auth`; auth timing is unchanged. `manualChunks` now relocates aws-amplify
// into its own `amplify-vendor` chunk for caching/parallelism.

// GW-13932: SSE streams need an Authorization header, which native EventSource cannot send.
installEventSourcePolyfill();

// Initialize Core Web Vitals collection (GW-218)
// Logs to console in development mode, can be extended for analytics
initWebVitals();

ReactDOM.createRoot(document.getElementById('root')!, {
  onUncaughtError: reactErrorHandler(),
  onCaughtError: reactErrorHandler(),
  onRecoverableError: reactErrorHandler(),
}).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="dark" storageKey="gw-theme">
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// GW-11884: Defer boot SDKs that are not needed for first paint until the browser is
// idle after the initial render, keeping them off the critical boot path.
//
// Trade-offs (accepted, per GW-11884):
//   - CloudWatch RUM (pure telemetry): telemetry for the earliest / slowest part of
//     load is not captured. Core Sentry error+trace capture (eager, instrument.ts)
//     still covers early-boot errors.
//   - Sentry Session Replay: replays begin after first paint rather than at t=0.
//     Error/trace capture is unaffected — only Replay is deferred, not core init.
function whenIdle(callback: () => void): void {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 3000 });
  } else {
    // Safari < 17 has no requestIdleCallback; fall back to a macrotask after paint.
    setTimeout(callback, 0);
  }
}

whenIdle(() => {
  // Initialize CloudWatch RUM (GW-94) after first paint.
  void import('./lib/awsRum.util').then((module) => module.initializeRum());
  // Load Sentry Session Replay (GW-11884) after first paint.
  void import('./lib/sentryReplay.util').then((module) => module.loadSentryReplay());
});
